import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import type { Errors } from '@inertiajs/core'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BusinessShareClass, BusinessShareholder } from '../../types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  basePath: string
  shareClasses: BusinessShareClass[]
  shareholders: BusinessShareholder[]
}

export function CapTableTransactionDialog({ open, onOpenChange, businessId, basePath, shareClasses, shareholders }: Props) {
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, processing, reset, clearErrors } = useForm({
    transaction_type: 'issuance',
    share_class_id: '',
    shareholder_id: '',
    from_shareholder_id: '',
    shares: '',
    price_per_share_cents: '',
    effective_date: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    if (open) {
      reset()
      setApiError(null); clearErrors()
    }
  }, [open])

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) { reset(); clearErrors() }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setApiError(null)
    const resourcePath = `${basePath}/businesses/${businessId}/cap_table_transactions`
    const payload = {
      ...data,
      shares: data.shares ? Number(data.shares) : '',
      price_per_share_cents: data.price_per_share_cents ? Number(data.price_per_share_cents) : '',
      from_shareholder_id: data.transaction_type === 'transfer' ? data.from_shareholder_id : null
    }
    const options = {
      data: { cap_table_transaction: payload },
      onSuccess: () => handleClose(true),
      onError: (errors: Errors) => setApiError(Object.values(errors).join(', '))
    }
    post(resourcePath, options)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-6"><DialogTitle>Record Transaction</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {apiError && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{apiError}</div>}
          <div className="space-y-2">
            <Label htmlFor="tx_type">Transaction Type <span className="text-destructive">*</span></Label>
            <Select value={data.transaction_type} onValueChange={v => setData('transaction_type', v)}>
              <SelectTrigger id="tx_type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="issuance">Issuance</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="cancellation">Cancellation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx_share_class">Share Class <span className="text-destructive">*</span></Label>
            <Select value={data.share_class_id} onValueChange={v => setData('share_class_id', v)}>
              <SelectTrigger id="tx_share_class"><SelectValue placeholder="Select share class" /></SelectTrigger>
              <SelectContent>
                {shareClasses.map(sc => (
                  <SelectItem key={sc.id} value={sc.id}>{sc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx_shareholder">
              {data.transaction_type === 'transfer' ? 'To Shareholder' : 'Shareholder'} <span className="text-destructive">*</span>
            </Label>
            <Select value={data.shareholder_id} onValueChange={v => setData('shareholder_id', v)}>
              <SelectTrigger id="tx_shareholder"><SelectValue placeholder="Select shareholder" /></SelectTrigger>
              <SelectContent>
                {shareholders.map(sh => (
                  <SelectItem key={sh.id} value={sh.id}>{sh.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {data.transaction_type === 'transfer' && (
            <div className="space-y-2">
              <Label htmlFor="tx_from_shareholder">From Shareholder <span className="text-destructive">*</span></Label>
              <Select value={data.from_shareholder_id} onValueChange={v => setData('from_shareholder_id', v)}>
                <SelectTrigger id="tx_from_shareholder"><SelectValue placeholder="Select source shareholder" /></SelectTrigger>
                <SelectContent>
                  {shareholders.filter(sh => sh.id !== data.shareholder_id).map(sh => (
                    <SelectItem key={sh.id} value={sh.id}>{sh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="tx_shares">Shares <span className="text-destructive">*</span></Label>
            <Input
              id="tx_shares"
              type="number"
              min="1"
              placeholder="e.g. 1000"
              value={data.shares}
              onChange={e => setData('shares', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx_price">Price Per Share (cents)</Label>
            <Input
              id="tx_price"
              type="number"
              min="0"
              placeholder="e.g. 100"
              value={data.price_per_share_cents}
              onChange={e => setData('price_per_share_cents', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tx_date">Effective Date <span className="text-destructive">*</span></Label>
            <Input
              id="tx_date"
              type="date"
              value={data.effective_date}
              onChange={e => setData('effective_date', e.target.value)}
              required
            />
          </div>
          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={processing}>{processing ? 'Recording...' : 'Record Transaction'}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
